import type { Asset, Entry } from 'src/types/contentful-sdk-compat';
import isPlainObject from 'lodash/isPlainObject';
import {
  CMS_IMAGE_QUALITY,
  ContentfulTemplates,
  isTesting,
} from 'src/lib/constants';
import { stripAeoMarkdownBodies } from './aeo-markdown';
import { assetIsImage } from './utils';
import { DEPLOY_ROOT } from 'src/env';

export const mergeQueryParams = (
  url: string,
  params: Record<string, string | undefined>,
) => {
  // Manually split since URLs routinely start with `//` which are not parseable by `new URL()`
  const [baseUrl, searchParams] = url.split('?');
  const workingSearchParams = new URLSearchParams(searchParams);

  for (const [key, value] of Object.entries(params)) {
    // Filter out undefined values for better DX
    if (value === undefined) {
      continue;
    }

    workingSearchParams.set(key, value);
  }

  // Note: `URLSearchParams.size` is newish and not supported by our Vitest setup, breaking
  // tests. Instead we just check if the stringified params are empty or not.
  // We should investigate feature parity between Next and Vitest.
  // @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/size
  const modifiedSearchParams = workingSearchParams.toString();

  return `${baseUrl}${
    modifiedSearchParams.length ? '?' : ''
  }${workingSearchParams.toString()}`;
};

export const hasQualityQuery = (url = ''): boolean => {
  const qualityRegex = new RegExp('q=[0-9]+');
  return qualityRegex.test(url);
};

export const optimizeCMSImage = (src: string): string => {
  if (hasQualityQuery(src)) {
    return src;
  }

  return mergeQueryParams(src, {
    q: CMS_IMAGE_QUALITY.toString(),
    fm:
      src.includes('.png') || src.includes('.jpeg') || src.includes('jpg')
        ? 'webp'
        : undefined,
  });
};

const assetBasePath = isTesting && DEPLOY_ROOT ? `/${DEPLOY_ROOT}` : '';

export const optimizeImage = (src: string) => {
  if (src === '' || src === undefined) {
    return src;
  }

  // Check for CMS images
  if (
    src.includes('images.ctfassets.net') ||
    src.includes('videos.ctfassets.net')
  ) {
    return optimizeCMSImage(src);
  }

  // Check for non-image files to exclude from optimization
  if (/^(https?:)?\/\/(assets.ctfassets.net)?/.test(src)) {
    return src;
  }

  // Handle testing env images
  // Check for src with hash before adding one
  const url = src.startsWith(assetBasePath) ? src : `${assetBasePath}${src}`;

  return url;
};

export const isAsset = (item: Entry<unknown> | Asset): item is Asset => {
  return assetIsImage(item);
};

export const isEntry = (
  item: Entry<unknown> | Asset,
): item is Entry<unknown> => {
  return item?.sys?.type === 'Entry';
};

export const optimizeAsset = (item: Asset): Asset => {
  if (!item?.fields?.file?.url) {
    console.warn(`Image URL missing: `, item); // eslint-disable-line no-console
    return item;
  }
  return {
    ...item,
    fields: {
      ...item.fields,
      file: {
        ...item?.fields?.file,
        url: optimizeImage(item?.fields?.file?.url),
      },
    },
  };
};

const optimizeEntryImages = (
  entry: Entry<unknown> | Entry<unknown>[] | Asset,
) => {
  // Page templates add their own query parameters
  // @ts-ignore
  const contentType = entry?.sys?.contentType?.sys?.id;
  if (contentType === ContentfulTemplates.PAGE) {
    return entry;
  }

  if (Array.isArray(entry)) {
    return entry.map(optimizeEntryImages);
  }

  if (isAsset(entry)) {
    return optimizeAsset(entry);
  }

  const fields = isEntry(entry) ? entry.fields : entry;
  if (!isPlainObject(fields)) {
    return fields;
  }

  const newFields = Object.keys(fields).reduce((acc, key) => {
    const value = fields[key];
    let newValue = value;
    if (Array.isArray(value)) {
      newValue = value.map(optimizeEntryImages);
    } else if (isEntry(value)) {
      newValue = optimizeEntryImages(value);
    } else if (isAsset(value)) {
      newValue = optimizeAsset(value);
    }
    acc[key] = newValue;
    return acc;
  }, {});

  if (isEntry(entry)) {
    return { ...entry, fields: newFields };
  }

  return newFields;
};

export const optimizeContentfulImages = (
  entry: Entry<unknown> | Entry<unknown>[] | Asset,
) => {
  // Contentful entries become Next.js page props after this transform. Keep
  // AEO-only source text out of the browser hydration payload while retaining
  // each entry ID for marker rendering and server-side markdown resolution.
  //
  // Strip once here rather than inside optimizeEntryImages, which would
  // re-walk every subtree at each level of nesting.
  stripAeoMarkdownBodies(entry);

  return optimizeEntryImages(entry);
};
